/**
 * Navigation Sidebar Component
 * Main sidebar container with:
 * - Global header (logo, org selector, collapse toggle)
 * - Vertical-grouped navigation sections driven by the config
 * - User profile footer
 * - Responsive behavior (desktop/mobile)
 */

import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useNavigationStore, useNavigationTree } from '../../store/navigationStore';
import { useAuthStore } from '../../store/authStore';
import { CollapsedNavigationItem } from './NavigationItem';
import { NavigationSection } from './NavigationSection';
import { cn } from '../../lib/utils';
import { NAVIGATION_CONFIG } from '../../config/navigation.config';
import { NavigationTreeNode } from '../../types/navigation.types';

// ─────────────────────────────────────────────────────────────────────────────
// Section descriptor — maps a header label + badge to a category key
// ─────────────────────────────────────────────────────────────────────────────
interface SectionDescriptor {
  /** category value used in NavigationConfig */
  category: string;
  /** Human-readable header label in the sidebar */
  label: string;
  /** Lucide icon name for the section header */
  icon: string;
  /** Whether the section is collapsed by default */
  defaultExpanded: boolean;
  /** Optional badge shown in the section header */
  badge?: import('../../types/navigation.types').NavigationBadge;
  /** Optional subtitle or description displayed below the label */
  subtitle?: string;
  /** Optional badge text displayed inline below the label */
  subBadge?: string;
  /** Only show this section when the predicate returns true */
  visibleWhen?: (opts: { isPlatformAdmin: boolean; isOrgAdmin: boolean; enabledModules: string[] }) => boolean;
  /** Render a horizontal divider above this section to visually separate verticals */
  dividerAbove?: boolean;
  /** Tailwind bg classes for the icon pod */
  iconBg?: string;
  /** Tailwind text-color classes for the icon inside the pod */
  iconColor?: string;
}

/** Ordered list of all sidebar sections.
 *  Vertical sections are shown/hidden based on `enabledModules`. */
const SECTION_DESCRIPTORS: SectionDescriptor[] = [
  // ── Intelligent Command Center ─────────────────────────────────────────────
  {
    category: 'Command Center',
    label: 'Command Center',
    icon: 'LayoutDashboard',
    defaultExpanded: true,
  },

  // ── AgroTech — AgroTech Intelligence ──────────────────────────────────────
  {
    category: 'AgroTech',
    label: 'AgroTech Intelligence',
    icon: 'Leaf',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-700 dark:text-green-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp1_agrotech'),
  },

  // ── AgroTech — Precision Planning (AgroTech Add-on) ───────────────────────
  {
    category: 'AgroTech Precision',
    label: 'Precision Planning',
    subBadge: 'AgroTech Add-on',
    icon: 'Target',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-700 dark:text-purple-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp2_agrotech_precision'),
    dividerAbove: false,
  },

  // ── Oil & Gas ─────────────────────────────────────────────────────────────
  {
    category: 'Oil & Gas',
    label: 'Oil & Gas',
    icon: 'Flame',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-700 dark:text-blue-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp3_oil_gas'),
  },

  // ── Infrastructure ────────────────────────────────────────────────────────
  {
    category: 'Infrastructure',
    label: 'Infrastructure',
    icon: 'Building',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-700 dark:text-amber-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp4_infrastructure'),
  },

  // ── Environmental ─────────────────────────────────────────────────────────
  {
    category: 'Environmental',
    label: 'Environmental',
    icon: 'TreePine',
    iconBg: 'bg-teal-100 dark:bg-teal-900/30',
    iconColor: 'text-teal-700 dark:text-teal-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp5_environmental'),
  },

  // ── Linear Assets (Power & Rail) ──────────────────────────────────────────
  {
    category: 'Linear Assets',
    label: 'Linear Assets',
    icon: 'Zap',
    iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
    iconColor: 'text-yellow-700 dark:text-yellow-400',
    defaultExpanded: false,
    visibleWhen: ({ enabledModules }) => enabledModules.includes('mvp6_linear_assets'),
  },

  // ── Organisation Settings ──────────────────────────────────────────────────
  {
    category: 'Organization Administration',
    label: 'Settings',
    icon: 'Settings',
    defaultExpanded: false,
    visibleWhen: ({ isOrgAdmin }) => isOrgAdmin,
  },

  // ── Platform Administration ─────────────────────────── bottom of nav ──────
  {
    category: 'Platform Administration',
    label: 'Platform',
    icon: 'Shield',
    defaultExpanded: false,
    badge: { type: 'text' as const, source: 'static' as const, value: 'Admin', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    visibleWhen: ({ isPlatformAdmin }) => isPlatformAdmin,
    dividerAbove: true,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helper — return the nav items that should be rendered inside a section.
//
// The section header button already carries the group label (e.g. "Platform",
// "Command Center"), so we must NOT also render the parent container node as a
// NavigationItem — that would duplicate the label.
//
// Rule:
//   • If a matching root node has NO path (it is a pure container/group), return
//     its children instead of the node itself.
//   • If a matching root node HAS a path (it is a real destination), include it
//     directly.
// Multiple root entries can share the same category (e.g. both AgroTech groups).
// ─────────────────────────────────────────────────────────────────────────────
function itemsForCategory(tree: NavigationTreeNode[], category: string): NavigationTreeNode[] {
  const result: NavigationTreeNode[] = [];
  for (const n of tree) {
    if (n.category !== category || !n.visible) continue;
    if (!n.path && n.children && n.children.length > 0) {
      // Container node — surface its children directly so the section header
      // is not duplicated by a NavigationItem with the same label.
      result.push(...n.children.filter((c) => c.visible));
    } else {
      result.push(n);
    }
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Nabhos logo — inline SVG so it renders correctly in both light and dark modes
// ─────────────────────────────────────────────────────────────────────────────
function NabhosLogo({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={className} aria-hidden="true">
      {/* Drone frame */}
      <path d="M 32,32 L 68,68 M 68,32 L 32,68" fill="none" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
      {/* Rotor nodes */}
      <circle cx="32" cy="32" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="68" cy="32" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="32" cy="68" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      <circle cx="68" cy="68" r="5" fill="none" stroke="#2563eb" strokeWidth="3" />
      {/* Eye outline */}
      <path d="M 36,50 Q 50,37 64,50 Q 50,63 36,50 Z" fill="white" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Iris */}
      <circle cx="50" cy="50" r="5.5" fill="#276749" />
      {/* Pupil */}
      <circle cx="50" cy="50" r="2.5" fill="#0f172a" />
      {/* Catchlight */}
      <circle cx="51.5" cy="48.5" r="1" fill="white" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export function NavigationSidebar() {
  const { user, permissions, currentOrganizationId, organizationModules } = useAuthStore();
  const {
    sidebarCollapsed,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    toggleSidebar,
    setNavigationItems,
    setContext,
    rebuildTree,
  } = useNavigationStore();

  const location = useLocation();
  const tree = useNavigationTree();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Initialize navigation on mount / user change
  useEffect(() => {
    if (user) {
      setNavigationItems(NAVIGATION_CONFIG);

      setContext({
        userId: user.id,
        userPermissions: permissions,
        userProperties: {},
        organizationId: currentOrganizationId || user.id,
        // Use modules from auth store (populated via login / org-switch API)
        organizationModules: organizationModules ?? ['mvp0_core', 'mvp0_enterprise'],
        organizationSettings: {},
        selectedNodeId: undefined,
        selectedNodeType: undefined,
        featureFlags: {},
        sessionId: user.id,
        timestamp: Date.now(),
      });

      useNavigationStore.setState({ treeLastUpdated: 0 });
      rebuildTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, permissions, currentOrganizationId, organizationModules]);

  const isPlatformAdmin = permissions.includes('*.*');
  const isOrgAdmin =
    isPlatformAdmin ||
    permissions.includes('organization:admin') ||
    permissions.includes('organization:*');

  const enabledModules: string[] = organizationModules ?? [];

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out',
        'bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800',
        'flex flex-col',
        // Desktop: controlled by collapse toggle
        sidebarCollapsed ? 'lg:w-20' : 'lg:w-64',
        // Mobile: slide in/out; always full width (w-64) when open
        'w-64',
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}
      aria-label="Main navigation"
    >
      {/* ── Global Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
        {!sidebarCollapsed ? (
          <Link
              to="/dashboard"
              className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white"
            >
              <NabhosLogo className="w-14 h-14 flex-shrink-0" />
              <div className="flex flex-col">
                <span>Nabhos</span>
                <span className="text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight">AI eye in the sky</span>
              </div>
            </Link>
          ) : (
            <Link to="/dashboard" aria-label="Nabhos home">
              <NabhosLogo className="w-14 h-14" />
            </Link>
        )}

        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <Icons.ChevronRight className="w-5 h-5 text-gray-500" />
          ) : (
            <Icons.ChevronLeft className="w-5 h-5 text-gray-500" />
          )}
        </button>
      </div>

      {/* ── Navigation Content ─────────────────────────────────────────────── */}
      <nav
        className="flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-1"
        aria-label="Navigation menu"
      >
        {sidebarCollapsed ? (
          /* Collapsed view — icons only */
          <div className="space-y-2 px-2">
            {tree.map((node) => (
              <CollapsedNavigationItem key={node.id} node={node} />
            ))}
          </div>
        ) : (
          /* Expanded view — vertical-grouped sections */
          <div className="space-y-4 px-3">
            {SECTION_DESCRIPTORS.map((descriptor) => {
              // Apply visibility guard
              if (
                descriptor.visibleWhen &&
                !descriptor.visibleWhen({ isPlatformAdmin, isOrgAdmin, enabledModules })
              ) {
                return null;
              }

              const items = itemsForCategory(tree, descriptor.category);

              // If the section has no visible items, skip it entirely
              if (items.length === 0) return null;

              return (
                <div
                  key={descriptor.category}
                  className={descriptor.dividerAbove ? 'pt-3 border-t border-gray-200 dark:border-gray-700' : undefined}
                >
                  <NavigationSection
                    id={`section-${descriptor.category.replace(/\s+/g, '-').toLowerCase()}`}
                    label={descriptor.label}
                    subtitle={descriptor.subtitle}
                    subBadge={descriptor.subBadge}
                    icon={descriptor.icon}
                    iconBg={descriptor.iconBg}
                    iconColor={descriptor.iconColor}
                    items={items}
                    defaultExpanded={descriptor.defaultExpanded}
                    badge={descriptor.badge}
                  />
                </div>
              );
            })}

            {/* Empty state */}
            {tree.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Icons.Inbox className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No navigation items available
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Contact your administrator
                </p>
              </div>
            )}
          </div>
        )}
      </nav>

    </aside>
  );
}

// Made with Bob
